import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { createNews, deleteNews, listNews, updateNews } from "../api/content";
import { capitalizeFirstLetter } from "../../utils/letters";
import { convertToWebp } from "../../utils/image";
import { formatDate } from "../../utils/date";
import DOMPurify from "dompurify";
import Modal from "../components/Modal";
import ConfirmModal from "../components/ConfirmModal";
import RichTextEditor from "../components/RichTextEditor";
import StatusBadge from "../components/StatusBadge";

const emptyForm = {
	titulo: "",
	bajada: "",
	cuerpo: "",
	visible: true,
	autor_nombre: "",
	fecha: "",
	imagen_portada: null,
	imagen_portada2: null,
	imagen_portada3: null,
};

const SAVE_FALLBACK_MESSAGE =
	"No se pudo guardar. Revisa los campos obligatorios.";
const NEWS_ROWS_PER_PAGE_DEFAULT = 10;
const NEWS_ALLOWED_ROWS = [10, 25, 50, 100];

const NEWS_SORT_OPTIONS = [
	{ value: "titulo", label: "Titulo" },
	{ value: "autor_nombre", label: "Autor" },
	{ value: "fecha_publicacion", label: "Publicada" },
	{ value: "visible", label: "Visible" },
];

const NEWS_ALLOWED_SORTS = new Set(
	NEWS_SORT_OPTIONS.map((option) => option.value),
);
const NEWS_ALLOWED_DIRECTIONS = new Set(["asc", "desc"]);

function parsePositiveInt(rawValue, fallbackValue) {
	const parsed = Number(rawValue);
	if (!Number.isInteger(parsed) || parsed < 1) return fallbackValue;
	return parsed;
}

function parseRows(rawValue) {
	const parsed = Number(rawValue);
	if (!NEWS_ALLOWED_ROWS.includes(parsed)) return NEWS_ROWS_PER_PAGE_DEFAULT;
	return parsed;
}

function parseSort(rawValue) {
	if (!NEWS_ALLOWED_SORTS.has(rawValue)) return "fecha_publicacion";
	return rawValue;
}

function parseDirection(rawValue) {
	if (!NEWS_ALLOWED_DIRECTIONS.has(rawValue)) return "desc";
	return rawValue;
}

function getFirstApiMessage(value) {
	if (Array.isArray(value)) return value[0] ?? "";
	if (typeof value === "string") return value;
	return "";
}

function getApiFieldErrors(data) {
	const next = {};
	if (!data || typeof data !== "object") return next;

	const keys = [
		"titulo",
		"bajada",
		"cuerpo",
		"autor_nombre",
		"fecha",
		"fecha_publicacion",
		"imagen_portada",
	];

	for (const key of keys) {
		const message = getFirstApiMessage(data[key]);
		if (message) {
			if (key === "fecha_publicacion") next["fecha"] = message;
			else next[key] = message;
		}
	}

	let detail = "";
	if (typeof data.detail === "string") {
		detail = data.detail;
	} else if (Array.isArray(data.non_field_errors)) {
		detail = data.non_field_errors[0] ?? "";
	}

	if (detail && !next.titulo && /titulo|title/i.test(detail)) {
		next.titulo = detail;
	}

	return next;
}

function getPlainTextFromHtml(html) {
	if (!html) return "";
	const doc = new DOMParser().parseFromString(html, 'text/html');
	return doc.body.textContent || "";
}

function escapeRegExp(value) {
	return value.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

function highlightSearchMatch(value, query) {
	const text = (value ?? "").toString();
	const needle = query.trim();
	if (!needle) return text;

	const matcher = new RegExp(`(${escapeRegExp(needle)})`, "ig");
	const parts = text.split(matcher);
	let cursor = 0;

	return parts.map((part) => {
		const key = `${cursor}-${part}`;
		cursor += part.length;
		if (part.toLowerCase() === needle.toLowerCase()) {
			return (
				<mark className="search-highlight" key={`hit-${key}`}>
					{part}
				</mark>
			);
		}
		return <span key={`txt-${key}`}>{part}</span>;
	});
}

function sanitizeBodyHtml(html) {
	return DOMPurify.sanitize(html || "", {
		USE_PROFILES: { html: true },
		FORBID_TAGS: ["style", "script", "iframe", "object", "embed"],
		FORBID_ATTR: ["style", "onerror", "onload", "onclick"],
	});
}

export default function NewsList() {
	const [searchParams, setSearchParams] = useSearchParams();
	const [items, setItems] = useState([]);
	const [search, setSearch] = useState(() => searchParams.get("q") || "");
	const [loading, setLoading] = useState(false);
	const [rowsPerPage, setRowsPerPage] = useState(() =>
		parseRows(searchParams.get("rows")),
	);
	const [currentPage, setCurrentPage] = useState(() =>
		parsePositiveInt(searchParams.get("page"), 1),
	);
	const [sortField, setSortField] = useState(() =>
		parseSort(searchParams.get("sort")),
	);
	const [sortDirection, setSortDirection] = useState(() =>
		parseDirection(searchParams.get("dir")),
	);
	const [alert, setAlert] = useState(null);
	const [imagePreviewSrc, setImagePreviewSrc] = useState(null);
	const [imagePreviewSrc2, setImagePreviewSrc2] = useState(null);
	const [imagePreviewSrc3, setImagePreviewSrc3] = useState(null);
	const [previewMainImage, setPreviewMainImage] = useState(null);
	const [modalOpen, setModalOpen] = useState(false);
	const [editing, setEditing] = useState(null);
	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewItem, setPreviewItem] = useState(null);
	const [form, setForm] = useState(emptyForm);
	const [saving, setSaving] = useState(false);
	const [itemToDelete, setItemToDelete] = useState(null);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [fieldErrors, setFieldErrors] = useState({});


	function openPreview(item) {
		setPreviewItem(item);
        setPreviewMainImage(item.imagen_portada || item.imagen_url || "/img/course/1.webp");
		setPreviewOpen(true);
	}

	function closePreview() {
		setPreviewOpen(false);
		setPreviewItem(null);
	}

	useEffect(() => {
		if (form.imagen_portada instanceof File) {
			const url = URL.createObjectURL(form.imagen_portada);
			setImagePreviewSrc(url);
			return () => URL.revokeObjectURL(url);
		}
		setImagePreviewSrc(editing?.imagen_portada ?? null);
	}, [form.imagen_portada, editing?.imagen_portada]);

	useEffect(() => {
		if (form.imagen_portada2 instanceof File) {
			const url = URL.createObjectURL(form.imagen_portada2);
			setImagePreviewSrc2(url);
			return () => URL.revokeObjectURL(url);
		}
		setImagePreviewSrc2(editing?.imagen_portada2 ?? null);
	}, [form.imagen_portada2, editing?.imagen_portada2]);

	useEffect(() => {
		if (form.imagen_portada3 instanceof File) {
			const url = URL.createObjectURL(form.imagen_portada3);
			setImagePreviewSrc3(url);
			return () => URL.revokeObjectURL(url);
		}
		setImagePreviewSrc3(editing?.imagen_portada3 ?? null);
	}, [form.imagen_portada3, editing?.imagen_portada3]);

	async function refresh() {
		setLoading(true);
		try {
			const data = await listNews({});
			setItems(Array.isArray(data) ? data : (data.results ?? []));
		} catch {
			setAlert({
				type: "danger",
				msg: "No se pudieron cargar las noticias.",
			});
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return items;
		return items.filter((n) => {
			const publishedAt = n.fecha_publicacion
				? formatDate(n.fecha_publicacion)
				: "";
			const visibleLabel = n.visible ? "si" : "no";
			return (
				(n.titulo ?? "").toLowerCase().includes(q) ||
				(n.autor_nombre ?? "").toLowerCase().includes(q) ||
				publishedAt.toLowerCase().includes(q) ||
				visibleLabel.includes(q)
			);
		});
	}, [items, search]);

	const sortedNews = useMemo(() => {
		const directionFactor = sortDirection === "asc" ? 1 : -1;
		const sorted = [...filtered];

		sorted.sort((left, right) => {
			if (sortField === "visible") {
				const leftValue = left.visible ? 1 : 0;
				const rightValue = right.visible ? 1 : 0;
				return (leftValue - rightValue) * directionFactor;
			}

			if (sortField === "fecha_publicacion") {
				const leftValue = left.fecha_publicacion
					? new Date(left.fecha_publicacion).getTime()
					: 0;
				const rightValue = right.fecha_publicacion
					? new Date(right.fecha_publicacion).getTime()
					: 0;
				return (leftValue - rightValue) * directionFactor;
			}

			const leftValue = (left[sortField] ?? "").toString().toLowerCase();
			const rightValue = (right[sortField] ?? "")
				.toString()
				.toLowerCase();
			return leftValue.localeCompare(rightValue, "es") * directionFactor;
		});

		return sorted;
	}, [filtered, sortDirection, sortField]);

	const totalRows = sortedNews.length;
	const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
	const safeCurrentPage = Math.min(currentPage, totalPages);
	const startIndex =
		totalRows === 0 ? 0 : (safeCurrentPage - 1) * rowsPerPage;
	const endIndex = Math.min(startIndex + rowsPerPage, totalRows);
	const paginatedNews = sortedNews.slice(startIndex, endIndex);

	const hasSearchQuery = search.trim().length > 0;
	const emptyNewsMessage = hasSearchQuery
		? "No hay resultados para la búsqueda actual."
		: items.length === 0
			? "No hay noticias registradas."
			: "No hay resultados para los filtros actuales.";

	let newsRows;
	if (loading) {
		newsRows = (
			<tr>
				<td colSpan="5" className="text-muted p-4">
					<span>Cargando...</span>
				</td>
			</tr>
		);
	} else if (paginatedNews.length === 0) {
		newsRows = (
			<tr>
				<td colSpan="5" className="text-muted p-4">
					<span>{emptyNewsMessage}</span>
				</td>
			</tr>
		);
	} else {
		newsRows = paginatedNews.map((n) => (
			<tr key={n.id}>
				<td className="fw-semibold">
					<span>
						{highlightSearchMatch(
							capitalizeFirstLetter(n.titulo),
							search,
						)}
					</span>
				</td>
				<td>
					<span>
						{highlightSearchMatch(
							capitalizeFirstLetter(n.autor_nombre || "—"),
							search,
						)}
					</span>
				</td>
				<td>
					<span>
						{highlightSearchMatch(
							formatDate(n.fecha_publicacion || "—"),
							search,
						)}
					</span>
				</td>
				<td>
					<StatusBadge
						active={Boolean(n.visible)}
						title="Noticia visible"
					/>
				</td>
				<td>
					<div className="d-flex justify-content-center align-items-center gap-1 flex-nowrap">
						<button
							type="button"
							className="btn-sm adm-see-btn d-inline-flex align-items-center justify-content-center border-0 text-nowrap"
							onClick={() => openPreview(n)}
							title="Detalle"
							aria-label="Detalle"
						>
							<i className="ri-eye-fill" />
						</button>
						<button
							type="button"
							className="btn-sm adm-upd-btn d-inline-flex align-items-center justify-content-center border-0 text-nowrap"
							onClick={() => openEdit(n)}
							title="Editar"
							aria-label="Editar"
						>
							<i className="ri-edit-fill" />
						</button>
						<button
							type="button"
							className="btn-sm adm-del-btn d-inline-flex align-items-center justify-content-center border-0 text-nowrap"
							onClick={() => openDeleteConfirm(n)}
							title="Eliminar"
							aria-label="Eliminar"
						>
							<i className="ri-delete-bin-line" />
						</button>
					</div>
				</td>
			</tr>
		));
	}

	useEffect(() => {
		const nextSearch = searchParams.get("q") || "";
		const nextRows = parseRows(searchParams.get("rows"));
		const nextPage = parsePositiveInt(searchParams.get("page"), 1);
		const nextSort = parseSort(searchParams.get("sort"));
		const nextDirection = parseDirection(searchParams.get("dir"));

		setSearch((prev) => (prev === nextSearch ? prev : nextSearch));
		setRowsPerPage((prev) => (prev === nextRows ? prev : nextRows));
		setCurrentPage((prev) => (prev === nextPage ? prev : nextPage));
		setSortField((prev) => (prev === nextSort ? prev : nextSort));
		setSortDirection((prev) =>
			prev === nextDirection ? prev : nextDirection,
		);
	}, [searchParams]);

	useEffect(() => {
		setCurrentPage(1);
	}, [search, rowsPerPage, sortField, sortDirection]);

	useEffect(() => {
		if (currentPage > totalPages) {
			setCurrentPage(totalPages);
		}
	}, [currentPage, totalPages]);

	useEffect(() => {
		const nextParams = new URLSearchParams(searchParams);

		if (search.trim()) nextParams.set("q", search.trim());
		else nextParams.delete("q");

		if (rowsPerPage !== NEWS_ROWS_PER_PAGE_DEFAULT) {
			nextParams.set("rows", String(rowsPerPage));
		} else {
			nextParams.delete("rows");
		}

		if (currentPage !== 1) nextParams.set("page", String(currentPage));
		else nextParams.delete("page");

		if (sortField !== "fecha_publicacion") nextParams.set("sort", sortField);
		else nextParams.delete("sort");

		if (sortDirection !== "desc") nextParams.set("dir", sortDirection);
		else nextParams.delete("dir");

		if (nextParams.toString() !== searchParams.toString()) {
			setSearchParams(nextParams, { replace: true });
		}
	}, [
		search,
		rowsPerPage,
		currentPage,
		sortField,
		sortDirection,
		searchParams,
		setSearchParams,
	]);

	function resetTableState() {
		setSearch("");
		setRowsPerPage(NEWS_ROWS_PER_PAGE_DEFAULT);
		setCurrentPage(1);
		setSortField("fecha_publicacion");
		setSortDirection("desc");
		setSearchParams(new URLSearchParams(), { replace: true });
	}

	function openCreate() {
		setEditing(null);
		setForm(emptyForm);
		setAlert(null);
		setFieldErrors({});
		setModalOpen(true);
	}

	function openEdit(item) {
		setEditing(item);
		setForm({
			titulo: item.titulo ?? "",
			bajada: item.bajada ?? "",
			cuerpo: item.cuerpo ?? "",
			visible: item.visible ?? true,
			autor_nombre: item.autor_nombre ?? "",
			fecha: item.fecha_publicacion ? item.fecha_publicacion.substring(0, 10) : "",
			imagen_portada: null,
			imagen_portada2: null,
			imagen_portada3: null,
		});
		setAlert(null);
		setFieldErrors({});
		setModalOpen(true);
	}

	function closeModal(forceDiscard = false) {
		const isExplicitDiscard = forceDiscard === true;
		if (saving) return;

		// Revisamos si el usuario ya había escrito algo o subido una foto
		const hasContent = form.titulo.trim() || form.bajada.trim() || getPlainTextFromHtml(form.cuerpo).trim() || form.imagen_portada;
		
		// Solo autoguardamos al hacer clic afuera si:
		// 1. Es una noticia nueva, o 2. Ya era un borrador. 
		// (Evitamos pasar a borrador una noticia que ya estaba pública accidentalmente)
		const isNewOrDraft = !editing || (editing && !editing.visible);

		if (!isExplicitDiscard && hasContent && isNewOrDraft) {
			onSubmit(null, true); // Guarda como borrador automáticamente
			return;
		}

		// Si el usuario da clic en "Descartar", limpiamos y cerramos
		setEditing(null);
		setForm(emptyForm);
		setModalOpen(false);
		setFieldErrors({});
	}

	function validateForm() {
		const err = {};
		if (!(form.titulo ?? "").trim())
			err.titulo = "El título es obligatorio.";
		if (!(form.bajada ?? "").trim())
			err.bajada = "La bajada (resumen) es obligatoria.";
		if (!getPlainTextFromHtml(form.cuerpo))
			err.cuerpo = "El contenido es obligatorio.";
		if (!(form.autor_nombre ?? "").trim())
			err.autor_nombre = "El autor es obligatorio.";
		if (!(form.fecha ?? "").trim())
			err.fecha = "La fecha del suceso es obligatorio.";
		setFieldErrors(err);
		return Object.keys(err).length === 0;
	}

	// ¡AQUÍ ESTÁ LA CORRECCIÓN! Agregamos ", isDraft = false"
	async function onSubmit(e, isDraft = false) {
		if (e && e.preventDefault) e.preventDefault();
		if (!isDraft && !validateForm()) return;
		setSaving(true);
		setAlert(null);
		try {
			const finalTitulo = form.titulo.trim() || "Borrador sin título";
			const finalBajada = form.bajada.trim() || "-";
			const finalCuerpo = getPlainTextFromHtml(form.cuerpo).trim() ? form.cuerpo : "<p>Borrador guardado automáticamente.</p>";
			const finalAutor = form.autor_nombre.trim() || "Administrador";
			const finalFecha = form.fecha || new Date().toISOString().split('T')[0];

			const sanitizedBody = sanitizeBodyHtml(isDraft ? finalCuerpo : form.cuerpo);
			
			const formData = new FormData();
			formData.append("titulo", isDraft ? finalTitulo : form.titulo);
			formData.append("bajada", isDraft ? finalBajada : form.bajada);
			formData.append("cuerpo", sanitizedBody);
			formData.append("visible", isDraft ? false : form.visible);
			formData.append("autor_nombre", isDraft ? finalAutor : form.autor_nombre);
			formData.append("fecha_publicacion", isDraft ? finalFecha : form.fecha);

			const processImage = async (fileState, fieldName) => {
				if (fileState instanceof File) {
					try {
						const webpFile = await convertToWebp(fileState, 0.8);
						formData.append(fieldName, webpFile);
					} catch (error) {
						console.error(`No se pudo convertir a WebP el campo ${fieldName}, enviando original`, error);
						formData.append(fieldName, fileState);
					}
				}
			};

			await processImage(form.imagen_portada, "imagen_portada");
			await processImage(form.imagen_portada2, "imagen_portada2");
			await processImage(form.imagen_portada3, "imagen_portada3");

			if (editing) {
				await updateNews(editing.id, formData);
				setAlert({
					type: "success",
					msg: isDraft ? "Borrador actualizado correctamente." : "Noticia actualizada correctamente.",
				});
			} else {
				await createNews(formData);
				setAlert({
					type: "success",
					msg: isDraft ? "Borrador guardado automáticamente." : "Noticia creada correctamente.",
				});
			}

			setEditing(null);
			setForm(emptyForm);
			setFieldErrors({});
			setModalOpen(false);
			await refresh();
		} catch (e) {
			const apiData = e.response?.data || {};
			const apiFieldErrors = getApiFieldErrors(apiData);
			setFieldErrors((prev) => ({ ...prev, ...apiFieldErrors }));

			let fallbackMsg = SAVE_FALLBACK_MESSAGE;
			if (typeof apiData?.detail === "string") {
				fallbackMsg = apiData.detail;
			} else if (Object.keys(apiFieldErrors).length === 0) {
				const unmapped = Object.entries(apiData)
					.map(([k, v]) => `${k}: ${getFirstApiMessage(v)}`)
					.join(" | ");
				if (unmapped) fallbackMsg = `Error del servidor: ${unmapped}`;
			}

			if (Object.keys(apiFieldErrors).length === 0) {
				setAlert({
					type: "danger",
					msg: fallbackMsg,
				});
			} else {
				setAlert(null);
			}
		} finally {
			setSaving(false);
		}
	}

	function openDeleteConfirm(item) {
		setItemToDelete(item);
	}

	async function confirmDelete() {
		if (!itemToDelete) return;
		setDeleteLoading(true);
		try {
			await deleteNews(itemToDelete.id);
			setAlert({
				type: "success",
				msg: "Noticia eliminada correctamente.",
			});
			setItemToDelete(null);
			await refresh();
		} catch {
			setAlert({
				type: "danger",
				msg: "No se pudo eliminar. Puede estar siendo usada en otra parte.",
			});
		} finally {
			setDeleteLoading(false);
		}
	}

	return (
		<div className="container-fluid">
			<div className="container-principal">
				<div>
					<h2 className="m-0">Noticias</h2>
				</div>

				<div className="admin-actions-wrapper w-100">
					<button
						className="admin-btn text-nowrap"
						onClick={openCreate}
					>
						<span>Nueva noticia</span>
					</button>
				</div>
			</div>

			{alert && (
				<div
					className={`alert alert-${alert.type} mt-3 mb-0`}
					role="alert"
				>
					<span>{alert.msg}</span>
				</div>
			)}

			<div className="card mt-3 shadow-sm">
				<div className="card-body p-0">
					<div className="table-toolbar table-toolbar-locked">
						<div className="toolbar-group toolbar-pagination-group">
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => setCurrentPage(1)}
								disabled={safeCurrentPage === 1}
							>
								&lt;&lt;
							</button>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() =>
									setCurrentPage((prev) =>
										Math.max(prev - 1, 1),
									)
								}
								disabled={safeCurrentPage === 1}
							>
								&lt;
							</button>
							<select
								className="form-select toolbar-select toolbar-page-select"
								value={safeCurrentPage}
								onChange={(e) =>
									setCurrentPage(Number(e.target.value))
								}
								disabled={totalPages <= 1}
							>
								{Array.from(
									{ length: totalPages },
									(_, idx) => idx + 1,
								).map((page) => (
									<option key={page} value={page}>
										{page}
									</option>
								))}
							</select>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() =>
									setCurrentPage((prev) =>
										Math.min(prev + 1, totalPages),
									)
								}
								disabled={safeCurrentPage >= totalPages}
							>
								&gt;
							</button>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => setCurrentPage(totalPages)}
								disabled={safeCurrentPage >= totalPages}
							>
								&gt;&gt;
							</button>
						</div>

						<div className="search-group toolbar-search-group d-flex align-items-center gap-2">
							<input
								className="form-control"
								value={search} onChange={(e) => setSearch(e.target.value)}
								placeholder="Buscar por titulo, autor, publicada o visible"
								maxLength={120}
							/>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => {
									setSearch("");
									setCurrentPage(1);
								}}
								disabled={!search.trim()}
								title="Limpiar búsqueda"
								aria-label="Limpiar búsqueda"
							>
								<i className="ri-eraser-line" />
							</button>
						</div>

						<div className="toolbar-group toolbar-controls-group">
							<label className="d-flex align-items-center gap-2 mb-0">
								<span>Número de filas:</span>
								<select
									className="form-select toolbar-select"
									value={rowsPerPage}
									onChange={(e) =>
										setRowsPerPage(Number(e.target.value))
									}
								>
									<option value={10}>10</option>
									<option value={25}>25</option>
									<option value={50}>50</option>
									<option value={100}>100</option>
								</select>
							</label>

							<label className="d-flex align-items-center gap-2 mb-0">
								<span>Ordenar por:</span>
								<select
									className="form-select toolbar-select"
									value={sortField}
									onChange={(e) =>
										setSortField(e.target.value)
									}
								>
									{NEWS_SORT_OPTIONS.map((option) => (
										<option
											key={option.value}
											value={option.value}
										>
											{option.label}
										</option>
									))}
								</select>
							</label>
							<button
								type="button"
								className="toolbar-order-btn"
								onClick={() =>
									setSortDirection((prev) =>
										prev === "asc" ? "desc" : "asc",
									)
								}
							>
								{sortDirection === "asc" ? "Asc" : "Desc"}
							</button>

							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={resetTableState}
								title="Reiniciar tabla"
							>
								Reiniciar
							</button>
						</div>
					</div>

					<div className="table-responsive table-wrapper-scroll">
						<table className="table table-sm table-hover mb-0 align-middle table-organizations-nowrap">
							<thead className="table-light">
								<tr>
									<th>
										<span>Título</span>
									</th>
									<th>
										<span>Autor</span>
									</th>
									<th>
										<span>Publicada</span>
									</th>
									<th>
										<span>Visible</span>
									</th>
									<th
										className="text-center"
										style={{ width: 260 }}
									>
										<span>Acciones</span>
									</th>
								</tr>
							</thead>
							<tbody>{newsRows}</tbody>
						</table>
					</div>
					<div className="table-footer-summary px-3 py-2 border-top">
						<span>
							Mostrando {totalRows === 0 ? 0 : startIndex + 1} -{" "}
							{endIndex} de {totalRows} registros
						</span>
					</div>
				</div>
			</div>

			<Modal
				title="Detalle de noticia"
				open={previewOpen}
				onClose={closePreview}
				dialogClassName="modal-dialog modal-xl modal-dialog-scrollable"
				footer={
					<button className="adm-save-btn" onClick={closePreview}>
						<span>Cerrar</span>
					</button>
				}
			>
				{previewItem ? (() => {
                    // Calculamos las imágenes disponibles dentro de la función anónima del render
                    const availablePreviewImages = [
                        previewItem.imagen_portada || previewItem.imagen_url || "/img/course/1.webp",
                        previewItem.imagen_portada2,
                        previewItem.imagen_portada3
                    ].filter(Boolean);

                    return (
                        <section className="container-news px-2 px-md-3 py-2 mb-0">
                            <span className="news-date">
                                Por
                                <strong className="text-dark">
                                    {" "}
                                    {capitalizeFirstLetter(
                                        previewItem.autor_nombre || "Administración",
                                    )}
                                </strong>
                                {" | Somnus Iquique, "}
                                {previewItem.fecha_formateada ||
                                    previewItem.fecha_publicacion ||
                                    "-"}
                            </span>
                            <h1 className="news-title">
                                {capitalizeFirstLetter(previewItem.titulo || "")}
                            </h1>
                            <hr className="drop-divider-line" />

                            <div className="news-content">
                                {previewItem.bajada && (
                                    <p className="news-bajada">
                                        {capitalizeFirstLetter(previewItem.bajada)}
                                    </p>
                                )}
                                
                                {/* GALERÍA INTERACTIVA DE VISTA PREVIA */}
                                <div className="news-gallery-container mb-4">
                                    <div className="news-img-wrapper mb-3">
                                        <img
                                            src={previewMainImage}
                                            alt={capitalizeFirstLetter(previewItem.titulo || "Noticia")}
                                        />
                                    </div>

                                    {/* Fila de Miniaturas */}
                                    {availablePreviewImages.length > 1 && (
                                        <div className="d-flex gap-3 justify-content-center flex-wrap">
                                            {availablePreviewImages.map((imgSrc, index) => (
                                                <div 
                                                    key={index} 
                                                    onClick={() => setPreviewMainImage(imgSrc)}
                                                    style={{
                                                        width: '100px', 
                                                        height: '70px',
                                                        cursor: 'pointer',
                                                        borderRadius: '8px',
                                                        overflow: 'hidden',
                                                        border: previewMainImage === imgSrc ? '3px solid var(--light-blue)' : '3px solid transparent',
                                                        opacity: previewMainImage === imgSrc ? 1 : 0.6,
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                >
                                                    <img 
                                                        src={imgSrc} 
                                                        alt={`Miniatura ${index + 1}`} 
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div
                                    className="news-body-text"
                                    dangerouslySetInnerHTML={{
                                        __html: sanitizeBodyHtml(
                                            previewItem.cuerpo || "",
                                        ),
                                    }}
                                />
                            </div>
                        </section>
                    );
                })() : (
					<span>No hay información para mostrar.</span>
				)}
			</Modal>

			<Modal
				title={editing ? "Editar noticia" : "Nueva noticia"}
				open={modalOpen}
				onClose={closeModal}
				footer={
					<>
						<button
							className="btn adm-del-btn"
							onClick={() => closeModal(true)}
							disabled={saving}
						>
							<span>Cancelar</span>
						</button>
						<button
							className="btn adm-see-btn"
							onClick={(e) => onSubmit(e, true)}
							disabled={saving}
						>
							<span>{saving ? "Guardando..." : "Guardar Borrador"}</span>
						</button>

						<button
							className="adm-save-btn"
							onClick={(e) => onSubmit(e, false)}
							disabled={saving}
						>
							<span>{saving ? "Publicando..." : "Publicar"}</span>
						</button>
					</>
				}
			>
				{alert && (
					<div className={`alert alert-${alert.type}`} role="alert">
						<span>{alert.msg}</span>
					</div>
				)}

				<form onSubmit={onSubmit} className="row g-3">
					<div className="col-12">
						<label className="form-label">
							<span>Título</span>{" "}
							<span className="text-danger">*</span>
						</label>
						<input
							className={`form-control ${fieldErrors.titulo ? "is-invalid" : ""}`}
							value={form.titulo} onChange={(e) => setForm((p) => ({
								...p,
								titulo: e.target.value,
							}))
							}
							onInput={() => {
								if (!fieldErrors.titulo) return;
								setFieldErrors((p) => ({
									...p,
									titulo: undefined,
								}));
							}}
							placeholder="Ej: Nuevas políticas de privacidad"
							aria-invalid={!!fieldErrors.titulo}
						/>
						{fieldErrors.titulo && (
							<div className="invalid-feedback">
								{fieldErrors.titulo}
							</div>
						)}
					</div>

					<div className="col-12">
						<label className="form-label">
							<span>Bajada (resumen)</span>{" "}
							<span className="text-danger">*</span>
						</label>
						<textarea
							className={`form-control ${fieldErrors.bajada ? "is-invalid" : ""}`}
							rows={2}
							value={form.bajada} onChange={(e) => setForm((p) => ({
								...p,
								bajada: e.target.value,
							}))
							}
							onInput={() => {
								if (!fieldErrors.bajada) return;
								setFieldErrors((p) => ({
									...p,
									bajada: undefined,
								}));
							}}
							placeholder="Ej: Resumen breve que aparecerá en el listado de noticias"
							aria-invalid={!!fieldErrors.bajada}
						/>
						{fieldErrors.bajada && (
							<div className="invalid-feedback">
								{fieldErrors.bajada}
							</div>
						)}
					</div>

					<div className="col-12">
						<label className="form-label">
							<span>Contenido</span>{" "}
							<span className="text-danger">*</span>
						</label>
						<RichTextEditor
							value={form.cuerpo}
							onChange={(value) => {
								setForm((p) => ({
									...p,
									cuerpo: value,
								}));
								if (fieldErrors.cuerpo) {
									setFieldErrors((p) => ({
										...p,
										cuerpo: undefined,
									}));
								}
							}}
							placeholder="Escribe aquí el contenido completo de la noticia."
							error={!!fieldErrors.cuerpo}
						/>
						{fieldErrors.cuerpo && (
							<div className="invalid-feedback d-block">
								{fieldErrors.cuerpo}
							</div>
						)}
						<span className="text-muted d-block mt-1 fs-6 text-justify">
							Puedes aplicar títulos, listas, enlaces, citas y
							formato de texto desde la barra superior.
						</span>
					</div>

					<div className="col-12">
						<label className="form-label mb-0">
							<span>Imágenes de la noticia </span>
							<span className="text-muted fs-6 d-block mb-3">Máx. 2MB por imagen. Formato ideal: 1920 x 540 px (imagen horizontal).</span>
						</label>
						<div className="mb-3 border p-3 rounded">
							{/* IMAGEN 1 (Principal) */}
							<div className="mb-4">
								<label className="form-label">
									<span>Imagen Principal</span>
									{!editing && <span className="text-danger">*</span>}
								</label>
								<input
									type="file"
									accept="image/*"
									className={`form-control ${fieldErrors.imagen_portada ? "is-invalid" : ""}`}
									onChange={(e) => {
										const file = e.target.files?.[0] || null;
										const MAX_FILE_SIZE = 2 * 1024 * 1024;
										if (file && file.size > MAX_FILE_SIZE) {
											setFieldErrors((p) => ({ ...p, imagen_portada: "Máximo 2MB." }));
											setForm((p) => ({ ...p, imagen_portada: null }));
											e.target.value = "";
											return;
										}
										setForm((p) => ({ ...p, imagen_portada: file }));
										setFieldErrors((p) => ({ ...p, imagen_portada: undefined }));
									}}
								/>
								{fieldErrors.imagen_portada && <div className="invalid-feedback">{fieldErrors.imagen_portada}</div>}

								{imagePreviewSrc && (
									<div className="mt-2">
										<img src={imagePreviewSrc} alt="Vista previa 1" style={{ maxWidth: 100, maxHeight: 80, objectFit: "contain", borderRadius: 4 }} />
									</div>
								)}
							</div>

							<hr className="text-muted" />

							<div className="mb-4">
								<label className="form-label">
									<span>Imagen Secundaria 1 (Opcional)</span>
								</label>
								<input
									type="file"
									accept="image/*"
									className={`form-control ${fieldErrors.imagen_portada2 ? "is-invalid" : ""}`}
									onChange={(e) => {
										const file = e.target.files?.[0] || null;
										const MAX_FILE_SIZE = 2 * 1024 * 1024;
										if (file && file.size > MAX_FILE_SIZE) {
											setFieldErrors((p) => ({ ...p, imagen_portada2: "Máximo 2MB." }));
											setForm((p) => ({ ...p, imagen_portada2: null }));
											e.target.value = "";
											return;
										}
										setForm((p) => ({ ...p, imagen_portada2: file }));
										setFieldErrors((p) => ({ ...p, imagen_portada2: undefined }));
									}}
								/>
								{fieldErrors.imagen_portada2 && <div className="invalid-feedback">{fieldErrors.imagen_portada2}</div>}
								{imagePreviewSrc2 && (
									<div className="mt-2">
										<img src={imagePreviewSrc2} alt="Vista previa 1" style={{ maxWidth: 100, maxHeight: 80, objectFit: "contain", borderRadius: 4 }} />
									</div>
								)}
							</div>

							<hr className="text-muted" />

							<div>
								<label className="form-label">
									<span>Imagen Secundaria 2 (Opcional)</span>
								</label>
								<input
									type="file"
									accept="image/*"
									className={`form-control ${fieldErrors.imagen_portada3 ? "is-invalid" : ""}`}
									onChange={(e) => {
										const file = e.target.files?.[0] || null;
										const MAX_FILE_SIZE = 2 * 1024 * 1024;
										if (file && file.size > MAX_FILE_SIZE) {
											setFieldErrors((p) => ({ ...p, imagen_portada3: "Máximo 2MB." }));
											setForm((p) => ({ ...p, imagen_portada3: null }));
											e.target.value = "";
											return;
										}
										setForm((p) => ({ ...p, imagen_portada3: file }));
										setFieldErrors((p) => ({ ...p, imagen_portada3: undefined }));
									}}
								/>
								{fieldErrors.imagen_portada3 && <div className="invalid-feedback">{fieldErrors.imagen_portada3}</div>}
								{imagePreviewSrc3 && (
									<div className="mt-2">
										<img src={imagePreviewSrc3} alt="Vista previa 2" style={{ maxWidth: 100, maxHeight: 80, objectFit: "contain", borderRadius: 4, border: "1px solid #dee2e6" }} />
									</div>
								)}
							</div>
						</div>


					</div>

					<div className="col-md-6">
						<label className="form-label">
							<span>Autor</span>{" "}
							<span className="text-danger">*</span>
						</label>
						<input
							className={`form-control ${fieldErrors.autor_nombre ? "is-invalid" : ""}`}
							value={form.autor_nombre} onChange={(e) => setForm((p) => ({
								...p,
								autor_nombre: e.target.value,
							}))
							}
							onInput={() => {
								if (!fieldErrors.autor_nombre) return;
								setFieldErrors((p) => ({
									...p,
									autor_nombre: undefined,
								}));
							}}
							placeholder="Ej: Dirección Somnus"
							aria-invalid={!!fieldErrors.autor_nombre}
						/>
						{fieldErrors.autor_nombre && (
							<div className="invalid-feedback">
								{fieldErrors.autor_nombre}
							</div>
						)}
					</div>
					<div className="col-md-6">
						<label className="form-label">
							<span>Fecha</span>{" "}
							<span className="text-danger">*</span>
						</label>
						<input
							type="date"
							className={`form-control ${fieldErrors.fecha ? "is-invalid" : ""}`}
							value={form.fecha} onChange={(e) => setForm((p) => ({
								...p,
								fecha: e.target.value,
							}))
							}
							onInput={() => {
								if (!fieldErrors.fecha) return;
								setFieldErrors((p) => ({
									...p,
									fecha: undefined,
								}));
							}}
							placeholder=""
							aria-invalid={!!fieldErrors.fecha}
						/>
						{fieldErrors.fecha && (
							<div className="invalid-feedback">
								{fieldErrors.fecha}
							</div>
						)}
					</div>

					<div className="col-md-6 d-flex align-items-end">
						<div className="form-check">
							<input
								className="form-check-input"
								type="checkbox"
								checked={form.visible}
								onChange={(e) =>
									setForm((p) => ({
										...p,
										visible: e.target.checked,
									}))
								}
								id="newsVisible"
							/>
							<label
								className="form-check-label"
								htmlFor="newsVisible"
							>
								<span>Visible en el sitio</span>
							</label>
						</div>
					</div>

					<button type="submit" className="d-none">
						<span>enviar</span>
					</button>
				</form>
			</Modal>

			<ConfirmModal
				open={!!itemToDelete}
				onClose={() => !deleteLoading && setItemToDelete(null)}
				onConfirm={confirmDelete}
				title="Eliminar noticia"
				message={
					itemToDelete
						? `¿Eliminar la noticia "${itemToDelete.titulo}"? Esta acción no se puede deshacer.`
						: ""
				}
				confirmLabel="Eliminar"
				variant="danger"
				loading={deleteLoading}
			/>
		</div>
	);
}
